import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth";
import { useEffectiveTenantId } from "./impersonation";

export type Tenant = {
  tenantId: string;
  name: string;
  theme: { primary: string; logoUrl?: string };
  status: "active" | "suspended";
};

type TenantState = {
  tenant: Tenant | null;
  loading: boolean;
};

const TenantContext = createContext<TenantState>({ tenant: null, loading: false });

function adjustHover(hex: string): string {
  // Devuelve un color un pelín más oscuro para hover. Fallback al mismo si parse falla.
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const v = parseInt(m[1], 16);
  const r = Math.max(0, ((v >> 16) & 0xff) - 20);
  const g = Math.max(0, ((v >> 8) & 0xff) - 20);
  const b = Math.max(0, (v & 0xff) - 20);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function applyBrand(tenant: Tenant | null) {
  const root = document.documentElement.style;
  if (!tenant) {
    root.removeProperty("--brand-primary");
    root.removeProperty("--brand-primary-hover");
    return;
  }
  root.setProperty("--brand-primary", tenant.theme.primary);
  root.setProperty("--brand-primary-hover", adjustHover(tenant.theme.primary));
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { claims } = useAuth();
  const effectiveTenantId = useEffectiveTenantId();
  const [state, setState] = useState<TenantState>({ tenant: null, loading: false });
  void claims;

  useEffect(() => {
    const tenantId = effectiveTenantId;
    if (!tenantId) {
      setState({ tenant: null, loading: false });
      applyBrand(null);
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    const unsub = onSnapshot(
      doc(db, "tenants", tenantId),
      (snap) => {
        const data = snap.data();
        const t: Tenant | null = data
          ? {
              tenantId,
              name: data.name,
              theme: {
                primary: data.theme?.primary ?? "#4a6921",
                logoUrl: data.theme?.logoUrl ?? undefined
              },
              status: data.status
            }
          : null;
        setState({ tenant: t, loading: false });
        applyBrand(t);
      },
      () => setState({ tenant: null, loading: false })
    );
    return () => unsub();
  }, [effectiveTenantId]);

  return <TenantContext.Provider value={state}>{children}</TenantContext.Provider>;
}

export const useTenant = () => useContext(TenantContext);
